<template>
  <select
    v-if="decl.type === 'enum'"
    :value="value == null ? '' : value"
    :style="{ ...inputStyle, appearance: 'auto' }"
    @change="onSelect"
  >
    <option value="">—</option>
    <option v-for="v in decl.values || []" :key="v" :value="v">
      {{ (meta.enumMeta && meta.enumMeta[v] && meta.enumMeta[v].label) || v }}
    </option>
  </select>

  <div v-else-if="decl.type === 'boolean'" :style="{ display: 'flex', gap: '4px' }">
    <button
      v-for="[label, v] in boolOpts"
      :key="label"
      type="button"
      :style="{
        flex: 1,
        padding: '6px 8px',
        borderRadius: '7px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '12.5px',
        fontWeight: 700,
        border: `1px solid ${boolCur === v ? 'var(--p-primary-500)' : 'var(--p-content-border-color)'}`,
        background: boolCur === v ? 'var(--p-highlight-background)' : 'transparent',
        color: boolCur === v ? 'var(--p-text-color)' : 'var(--p-text-muted-color)',
      }"
      @click="change(v === '' ? null : v === 'true')"
    >
      {{ label }}
    </button>
  </div>

  <input
    v-else
    :type="inputType"
    :value="value == null ? '' : value"
    spellcheck="false"
    :style="{ ...inputStyle, fontFamily: decl.type === 'objectId' ? mono : 'inherit' }"
    @input="onInput"
  >
</template>

<script setup>
// Editable control per field type. Ported from instance-panel.jsx FieldControl.
// Emits `update:value` (and a plain `change`) with the coerced value:
//   enum    → string ("" cleared)
//   boolean → null | true | false (tri-state)
//   number  → null | Number
//   date    → string (yyyy-mm-dd)
//   default → string
import { computed } from 'vue'

const props = defineProps({
  fieldKey: { type: String, required: true },
  fieldDefinitions: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  // Whatever the field holds: a string, a number, a boolean, or a date as
  // a `yyyy-mm-dd` string. Null is the cleared value.
  value: { type: [String, Number, Boolean], default: null },
})
const emit = defineEmits(['update:value', 'change'])

const inputStyle = {
  width: '100%',
  minWidth: 0,
  padding: '7px 9px',
  borderRadius: '8px',
  border: '1px solid var(--p-content-border-color)',
  background: 'var(--surface-ground, transparent)',
  color: 'var(--p-text-color)',
  fontSize: '13px',
  fontFamily: 'inherit',
  outline: 'none',
}
const mono = 'var(--p-font-mono, ui-monospace, monospace)'

const decl = computed(() => (props.fieldDefinitions.fields || {})[props.fieldKey] || {})
const meta = computed(() => (props.fieldMeta || {})[props.fieldKey] || {})

function change(v) {
  emit('update:value', v)
  emit('change', v)
}

// boolean tri-state
const boolCur = computed(() =>
  props.value === true || props.value === 'true' ? 'true' : props.value === false || props.value === 'false' ? 'false' : '',
)
const boolOpts = [
  ['—', ''],
  ['Yes', 'true'],
  ['No', 'false'],
]

const inputType = computed(() => (decl.value.type === 'number' ? 'number' : decl.value.type === 'date' ? 'date' : 'text'))

function onSelect(e) {
  change(e.target.value)
}
function onInput(e) {
  if (decl.value.type === 'number') change(e.target.value === '' ? null : Number(e.target.value))
  else change(e.target.value)
}
</script>
